import React, { useState, useEffect } from 'react';

import config from 'config/config';
import useSpotConfig from '@host-panel/request/config/use-config';
import SpacerVertical from '../../common/spacer-vertical';
import HeroImage from '../../common/hero-image';
import GenericFaq from '../../common/faqs/generic-faq';
import TitleAndDescription, { HEADER_FONTS } from '../../common/description-component';
import PlatformSettings from './PlatformSettings';
import { BellLocation, CustomBundleConfig } from './types';

const NOTIFICATIONS_SDK_CUSTOM_BUNDLE_CONFIG = {
  assetModuleName: 'notifications-sdk',
  assetTenantConfig: 'tenant_config.notifications.floating_bell_enabled'
};

const GENERAL_FLOATING_ENABLED_KEY = 'tenant.notifications.floating_bell_enabled';

const DESKTOP_ENABLED_KEY = 'tenant.floating-bell.desktopEnabled';
const MOBILE_ENABLED_KEY = 'tenant.floating-bell.mobileEnabled';

const DESKTOP_LOCATION_KEY = 'tenant.floating-bell.desktopLocation';
const MOBILE_LOCATION_KEY = 'tenant.floating-bell.mobileLocation';

const LAUNCHER_CUSTOM_BUNDLES_KEY = 'tenant.launcher.immediateCustomBundlesConfig';

const LOCATION_OPTIONS = [
  {
    value: 'bottom-right',
    text: 'Bottom Right'
  },
  {
    value: 'bottom-left',
    text: 'Bottom Left'
  }
];

const toggle = (value: boolean) => !value;

const shouldAddNotificationsSdkConfig = launcherCustomBundles => {
  const { assetModuleName, assetTenantConfig } = NOTIFICATIONS_SDK_CUSTOM_BUNDLE_CONFIG;
  const found = launcherCustomBundles.find(
    customBundle =>
      customBundle.assetModuleName === assetModuleName &&
      customBundle.assetTenantConfig === assetTenantConfig
  );
  return !found;
};

const getWithNotificationsSdkBundleConfig = launcherCustomBundles => {
  return [
    ...(launcherCustomBundles as CustomBundleConfig[]),
    NOTIFICATIONS_SDK_CUSTOM_BUNDLE_CONFIG
  ];
};

const FloatingBell = () => {
  const [message, setMessage] = useState(null);
  const [isDesktopEnabled, setIsDesktopEnabled] = useState(false);
  const [desktopLocation, setDesktopLocation] = useState<BellLocation>('bottom-right');
  const [isMobileEnabled, setIsMobileEnabled] = useState(false);
  const [mobileLocation, setMobileLocation] = useState<BellLocation>('bottom-right');
  const [
    [
      tenantDesktopEnabled,
      tenantDesktopLocation,
      tenantMobileEnabled,
      tenantMobileLocation,
      launcherCustomBundles
    ],
    setConfig,
    { loading }
  ] = useSpotConfig([
    DESKTOP_ENABLED_KEY,
    DESKTOP_LOCATION_KEY,
    MOBILE_ENABLED_KEY,
    MOBILE_LOCATION_KEY,
    LAUNCHER_CUSTOM_BUNDLES_KEY
  ]);

  useEffect(() => {
    if (!loading) {
      setIsDesktopEnabled((tenantDesktopEnabled as boolean) ?? isDesktopEnabled);
      setDesktopLocation((tenantDesktopLocation as BellLocation) ?? desktopLocation);
      setIsMobileEnabled((tenantMobileEnabled as boolean) ?? isMobileEnabled);
      setMobileLocation((tenantMobileLocation as BellLocation) ?? mobileLocation);
    }
  }, [loading]);

  const saveSettings = async () => {
    const isSomeFloatingBellEnabled = [isDesktopEnabled, isMobileEnabled].some(Boolean);
    const nextConfig = [
      { key: DESKTOP_ENABLED_KEY, value: isDesktopEnabled },
      { key: DESKTOP_LOCATION_KEY, value: desktopLocation },
      { key: MOBILE_ENABLED_KEY, value: isMobileEnabled },
      { key: MOBILE_LOCATION_KEY, value: mobileLocation },
      { key: GENERAL_FLOATING_ENABLED_KEY, value: isSomeFloatingBellEnabled }
    ];

    if (isSomeFloatingBellEnabled && shouldAddNotificationsSdkConfig(launcherCustomBundles)) {
      // adding needed launcher tenant config so notifications-sdk bundle will be enabled
      nextConfig.push({
        key: LAUNCHER_CUSTOM_BUNDLES_KEY,
        value: getWithNotificationsSdkBundleConfig(launcherCustomBundles) as any
      });
    }

    await setConfig(nextConfig);

    setMessage('Settings successfully saved');

    setTimeout(() => {
      setMessage(null);
    }, 5000);
  };

  return (
    <>
      {message && (
        <div className="success-message">
          <div className="text">{message}</div>
        </div>
      )}
      <div className="floatingBellPage">
        <section className="main-section">
          <SpacerVertical size="40px" />
          <TitleAndDescription
            titleFont={HEADER_FONTS.Large}
            title="Floating Bell"
            description={
              <>
                <SpacerVertical size="20px" />
                <p>
                  Allow your users to easily see their notifications in real-time and access them
                  quickly from anywhere on your site.
                </p>
              </>
            }
          />

          <SpacerVertical size="20px" />

          <HeroImage
            src="https://spotim-prd-static-assets.s3.amazonaws.com/production/notifications-sdk/images/hero.png"
            alt="Floating Bell preview"
          />

          <SpacerVertical size="20px" />

          <TitleAndDescription title="Desktop Settings" titleFont={HEADER_FONTS.Medium} />

          <SpacerVertical size="-20px" />

          <PlatformSettings
            isEnabled={isDesktopEnabled}
            onToggle={() => setIsDesktopEnabled(toggle)}
            options={LOCATION_OPTIONS}
            selectedOption={LOCATION_OPTIONS.find(option => option.value === desktopLocation)}
            onOptionChange={option => setDesktopLocation(option.value as BellLocation)}
          />

          <SpacerVertical size="50px" />

          <TitleAndDescription
            title="Mobile Settings"
            titleFont={HEADER_FONTS.Medium}
            description="*width below 480px"
          />

          <SpacerVertical size="-20px" />

          <PlatformSettings
            isEnabled={isMobileEnabled}
            onToggle={() => setIsMobileEnabled(toggle)}
            options={LOCATION_OPTIONS}
            selectedOption={LOCATION_OPTIONS.find(option => option.value === mobileLocation)}
            onOptionChange={option => setMobileLocation(option.value as BellLocation)}
          />

          <SpacerVertical size="20px" />

          <div className="policy-button-wrapper">
            <button className="policy-button bell-policy-icon" onClick={saveSettings} disabled={loading}>
              Save & Publish
            </button>
          </div>

          <SpacerVertical size="20px" />
        </section>

        <GenericFaq>
          <div style={{ minHeight: '80vh' }}>
            <div className="faq">Any questions?</div>
            <div className="faq-description">
              <span>
                Visit our
                <a
                  className="faq-link"
                  href={config.api.urls.support}
                  target="_blank"
                  rel="noreferrer"
                >
                  {' '}
                  support{' '}
                </a>
                site or go ahead and contact our support team.
              </span>
            </div>
          </div>
        </GenericFaq>
      </div>
    </>
  );
};

export default FloatingBell;
